/* Course search for security.licensing-solutions.co.uk
 *
 * Reads live course dates from the Licensing Solutions Shopify store's public product
 * feed. No backend, no app, no invented data: if the store holds no security courses
 * yet, the page says dates are launching soon.
 *
 * Each course date is one Shopify product tagged `security`, carrying its details in
 * structured tags (see SHOPIFY-COURSE-IMPORT.md):
 *   security | city:Manchester | geo:53.4835_-2.2312 | start:2026-10-05 | end:2026-10-10
 *   provider:ABC Training | venue:City centre | firstaid:included | format:weekday
 * Price and availability come from the variant, so places can never be oversold.
 */
(() => {
  const SHOP = 'https://licensing-solutions.co.uk';
  const FEED = SHOP + '/products.json?limit=250';
  const POSTCODES = 'https://api.postcodes.io';
  const MILES = 3958.8;

  const root = document.getElementById('results');
  if (!root) return;

  const form = document.querySelector('.console');
  const chips = [...document.querySelectorAll('.filters .chip input')];
  const qs = new URLSearchParams(location.search);

  /* ---------- helpers ---------- */
  const tagOf = (tags, key) => {
    const hit = tags.find((t) => t.toLowerCase().startsWith(key + ':'));
    return hit ? hit.slice(key.length + 1).trim() : '';
  };
  const money = (p) => '£' + (p / 100).toFixed(2).replace(/\.00$/, '');
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const fmtDate = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
  };
  const fmtRange = (a, b) => {
    if (!a) return '';
    if (!b || b === a) return fmtDate(a);
    const s = new Date(a + 'T00:00:00'), e = new Date(b + 'T00:00:00');
    const same = s.getMonth() === e.getMonth();
    return fmtDate(a) + ' – ' + (same
      ? e.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
      : fmtDate(b));
  };
  const days = (a, b) => {
    if (!a || !b) return 0;
    return Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
  };
  const distance = (a, b) => {
    const r = (x) => (x * Math.PI) / 180;
    const dLat = r(b.lat - a.lat), dLon = r(b.lon - a.lon);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLon / 2) ** 2;
    return 2 * MILES * Math.asin(Math.sqrt(h));
  };

  /* ---------- load the store feed ---------- */
  const load = async () => {
    const out = [];
    for (let page = 1; page <= 4; page++) {
      const res = await fetch(FEED + '&page=' + page, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('feed ' + res.status);
      const batch = (await res.json()).products || [];
      out.push(...batch);
      if (batch.length < 250) break;
    }
    return out
      .filter((p) => (p.tags || []).some((t) => t.toLowerCase() === 'security'))
      .map((p) => {
        const tags = p.tags || [];
        const v = (p.variants || [])[0] || {};
        const geo = tagOf(tags, 'geo').split(/[_\/,]/).map(Number);   // Shopify splits tags on commas, so geo uses lat_lon
        const start = tagOf(tags, 'start'), end = tagOf(tags, 'end');
        return {
          id: v.id, handle: p.handle,
          course: p.product_type || p.title,
          city: tagOf(tags, 'city'),
          provider: tagOf(tags, 'provider'),
          venue: tagOf(tags, 'venue'),
          firstAid: tagOf(tags, 'firstaid'),
          format: tagOf(tags, 'format').toLowerCase(),
          start, end,
          startMs: start ? new Date(start + 'T00:00:00').getTime() : 0,
          days: days(start, end) || Number(tagOf(tags, 'days')) || 0,
          geo: geo.length === 2 && geo.every((n) => !isNaN(n)) ? { lat: geo[0], lon: geo[1] } : null,
          price: v.price != null ? Number(v.price) * 100 : null,  // products.json prices are decimal strings
          available: v.available !== false,
        };
      })
      .filter((e) => e.startMs >= new Date().setHours(0, 0, 0, 0))   // never show a date that has passed
      .sort((a, b) => a.startMs - b.startMs);
  };

  /* ---------- turn a typed location into a point, or a city name ---------- */
  const geocode = async (text) => {
    const q = text.trim();
    if (!q) return null;
    const looksPostcode = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d?[A-Z]{0,2}$/i.test(q);
    if (!looksPostcode) return { city: q.toLowerCase(), name: q };
    const outcodeOnly = !/\d[A-Z]{2}$/i.test(q.replace(/\s/g, ''));
    const url = outcodeOnly
      ? POSTCODES + '/outcodes/' + encodeURIComponent(q.replace(/\s/g, ''))
      : POSTCODES + '/postcodes/' + encodeURIComponent(q);
    try {
      const res = await fetch(url);
      if (!res.ok) return { city: q.toLowerCase(), name: q, unknownPostcode: true };
      const r = (await res.json()).result;
      return { lat: r.latitude, lon: r.longitude, name: r.admin_district || r.outcode || q };
    } catch {
      return { city: q.toLowerCase(), name: q, unknownPostcode: true };
    }
  };

  /* ---------- filtering ---------- */
  const matches = (e, f) => {
    if (f.course) {
      const a = e.course.toLowerCase(), b = f.course.toLowerCase();
      if (a !== b && !a.includes(b) && !b.includes(a)) return false;
    }
    if (f.place) {
      if (f.place.city) {
        if (!e.city.toLowerCase().includes(f.place.city)) return false;
      } else if (f.place.lat != null) {
        if (!e.geo) return false;
        e.miles = distance(f.place, e.geo);
        if (e.miles > f.radius) return false;
      }
    }
    if (f.from && e.startMs < f.from) return false;
    if (f.to && e.startMs > f.to) return false;
    if (f.weekday && e.format && !e.format.includes('weekday')) return false;
    if (f.weekend && e.format && !e.format.includes('weekend')) return false;
    if (f.firstAid && e.firstAid !== 'included') return false;
    return true;
  };

  const readFilters = async () => {
    // Course and city pages have no form: they pin their own filter on the container.
    const pinned = root.dataset;
    const val = (n) => {
      const el = form && form.querySelector('[name="' + n + '"]');
      if (el && el.value) return el.value;
      if (n === 'course' && pinned.course) return pinned.course;
      if (n === 'location' && pinned.location) return pinned.location;
      return el ? el.value : '';
    };
    const when = val('when') || qs.get('when') || '';
    const now = new Date().setHours(0, 0, 0, 0);
    const f = { course: val('course') || qs.get('course') || '', radius: 25 };
    if (when === 'Next 7 days') { f.from = now; f.to = now + 7 * 86400000; }
    else if (when === 'Next 30 days') { f.from = now; f.to = now + 30 * 86400000; }
    else if (when === 'Weekends only') f.weekend = true;
    else if (when === 'Weekdays only') f.weekday = true;
    const on = (label) => chips.some((c) => c.checked && c.parentElement.textContent.trim() === label);
    if (on('Weekday')) f.weekday = true;
    if (on('Weekend')) f.weekend = true;
    if (on('First aid included')) f.firstAid = true;
    if (on('Within 10 miles')) f.radius = 10;
    if (on('Next 30 days')) { f.from = now; f.to = now + 30 * 86400000; }
    f.place = await geocode(val('location') || qs.get('location') || '');
    return f;
  };

  /* ---------- rendering ---------- */
  const soon = (msg) => `<div class="board__head"><h2 class="board__title"><span class="live live--soon"></span>Live dates launching soon</h2>
      <p class="board__note">Provider inventory is being onboarded now.</p></div>
    <div class="soon"><p class="soon__lead">${msg}</p>
      <div class="soon__acts"><a class="btn btn--go" href="/contact/">Tell us what you're looking for</a>
      <a class="btn btn--ghost" href="/training-providers/">List your courses</a></div></div>`;

  const card = (e) => {
    const bits = [
      ['Starts', fmtRange(e.start, e.end)],
      ['Format', (e.days ? e.days + (e.days === 1 ? ' day' : ' days') : '') + (e.format ? ' · ' + e.format : '')],
      ['City', e.city || '—'],
      ['Venue area', e.venue || 'Shown on booking'],
      ['Provider', e.provider || 'Named on booking'],
      ['First aid', e.firstAid === 'included' ? 'Included' : e.firstAid === 'addon' ? 'Available as an add-on' : 'Must already be held'],
    ].map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join('');
    const status = e.available
      ? '<span class="st st--go">Places available</span>'
      : '<span class="st st--wait">Sold out</span>';
    const near = e.miles != null ? `<span class="ev__near">${e.miles.toFixed(0)} miles away</span>` : '';
    const book = e.available
      ? `<a class="btn btn--go btn--sm" href="${SHOP}/cart/${e.id}:1" rel="nofollow">Book this date</a>`
      : '<span class="btn btn--ghost btn--sm" aria-disabled="true">Sold out</span>';
    return `<article class="ev">
      <div class="ev__top"><span class="ev__course">${esc(e.course)}</span>${status}</div>
      <dl class="ev__meta">${bits}</dl>
      <div class="ev__foot"><span class="ev__price">${e.price != null ? money(e.price) + ' <small>+ VAT</small>' : 'Price on booking'}${near}</span>${book}</div>
    </article>`;
  };

  const render = (list, f) => {
    if (!list.length) {
      const where = f && f.place ? (f.place.name || f.place.city || '') : '';
      root.innerHTML = soon(where
        ? `No dates near ${esc(where)} yet. Tell us what you need and we will match you to a provider as inventory comes online.`
        : 'Course dates appear here as soon as training centres are connected. Every listing will name the provider, venue area, price and first-aid status before you pay.');
      return;
    }
    const head = `<p class="results__count">${list.length} ${list.length === 1 ? 'date' : 'dates'} found${
      f.place && f.place.lat != null ? ` within ${f.radius} miles of ${esc(f.place.name)}` : ''}</p>`;
    root.innerHTML = head + '<div class="evgrid">' + list.map(card).join('') + '</div>';
  };

  /* ---------- run ---------- */
  let all = null, seq = 0;
  const run = async () => {
    const token = ++seq;          // a later search always wins, however slow the earlier one is
    root.setAttribute('aria-busy', 'true');
    try {
      if (!all) all = await load();
      const f = await readFilters();
      if (token !== seq) return;  // superseded while we were geocoding
      render(all.filter((e) => matches(e, f)), f);
    } catch (err) {
      if (token === seq) root.innerHTML = soon('We could not load course dates just now. Please try again shortly, or tell us what you are looking for.');
    }
    if (token === seq) root.removeAttribute('aria-busy');
  };

  // Prefill from the URL so the homepage search lands here with its choices intact
  if (form) {
    ['course', 'location', 'when'].forEach((n) => {
      const el = form.querySelector('[name="' + n + '"]');
      if (el && qs.get(n)) el.value = qs.get(n);
    });
    form.addEventListener('submit', (ev) => { ev.preventDefault(); run(); });
    form.addEventListener('change', run);
  }
  chips.forEach((c) => c.addEventListener('change', run));
  run();
})();
